"use client";

import cn from "clsx";
import React, { ChangeEvent, useState } from "react";
import {
    FieldDescription,
    getFieldDescriptionId,
} from "../form-fields/field-description";
import { FieldLabel } from "../form-fields/field-label";
import { FieldProps } from "../form-fields/field-props";
import { FieldWrapper } from "../form-fields/field-wrapper";
import styles from "./Textarea.module.scss";
import { TextareaBody, TextareaBodyProps } from "./TextareaBody";

export type TextareaProps = Omit<TextareaBodyProps, "hasError"> &
    FieldProps & {
        /**
         * Force controlled mode sets the initial value to empty string
         */
        forceControlledMode?: boolean;
        /**
         * Whether to show the max length text
         */
        showMaxLengthText?: boolean;
    };
export const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
    (
        {
            label,
            required = false,
            errorText,
            descriptionText,
            hideErrorText,
            value,
            forceControlledMode,
            defaultValue,
            maxLength,
            onChange,
            showOptionalValidationText,
            hideMarker,
            hideErrorIcon,
            fieldId: id,
            disabled,
            hideLabel,
            showMaxLengthText,
            ...textareaProps
        },
        ref,
    ) => {
        const hasError = !!errorText;
        const val = forceControlledMode || value ? value ?? "" : undefined;
        const initialLength = (value ?? defaultValue)?.toString().length ?? 0;
        const [length, setLength] = useState(initialLength);
        const handleChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
            onChange?.(e);
            setLength(e.target.value.length);
        };
        const remainingCharacters =
            maxLength && Math.max(maxLength - length, 0);
        return (
            <FieldWrapper
                disabled={disabled}
                data-has-error={hideMarker}
                fieldLabel={
                    <FieldLabel
                        required={required}
                        fieldId={id}
                        hideMarker={hideMarker}
                        hideLabel={hideLabel}
                        showOptionalValidationText={showOptionalValidationText}
                        hasError={hasError}
                        disabled={disabled}
                    >
                        {label}
                    </FieldLabel>
                }
                fieldDescription={
                    <FieldDescription
                        fieldId={id}
                        errorText={errorText}
                        hideErrorIcon={hideErrorIcon}
                        hideErrorText={hideErrorText}
                        disabled={disabled}
                        characterCount={
                            !!maxLength &&
                            showMaxLengthText && (
                                <div
                                    aria-live="polite"
                                    className={cn([
                                        styles.characterCount,
                                        {
                                            [styles[
                                                "characterCount--limit-reached"
                                            ]]: !remainingCharacters,
                                        },
                                    ])}
                                >
                                    {`${remainingCharacters}/${maxLength} characters remaining`}
                                </div>
                            )
                        }
                    >
                        {descriptionText}
                    </FieldDescription>
                }
            >
                <TextareaBody
                    {...textareaProps}
                    ref={ref as any}
                    id={id}
                    hasError={hasError}
                    value={val}
                    onChange={handleChange}
                    disabled={disabled}
                    maxLength={maxLength}
                    aria-describedby={getFieldDescriptionId(id)}
                />
            </FieldWrapper>
        );
    },
);

Textarea.displayName = "Textarea";
